import { useState, type RefObject, type HTMLAttributes } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { cn } from '@virginexperiencedays/tailwind';
import { screens } from '@virginexperiencedays/tailwind/screens';
import { useScrollToRef, useWindowWidth } from '@virginexperiencedays/hooks';

import { Container } from '@virginexperiencedays/components-v2/src/layout/Container';
import { Section } from '@virginexperiencedays/components-v2/src/layout/Section';
import { SectionHeading } from '@virginexperiencedays/components-v2/src/typography/SectionHeading';
import { DisplayHeading } from '@virginexperiencedays/components-v2/src/typography/DisplayHeading';
import { getProductCardPrices } from '@virginexperiencedays/components-v2/src/utils/productPricing';

import {
  trackPdp,
  trackPdpReview,
  trackPdpMediaGrid,
  trackPdpPayment,
  PdpInteraction,
  PdpModules,
} from '../../../../libs/tracking/pdp';

import { AdditionalDetailsWidget } from '../Widgets/AdditionalDetailsWidget';
import { CategoryPillsWidget } from '../Widgets/CategoryPillsWidget';
import { MediaGridWidget } from '../Widgets/MediaGridWidget';
import { MetaWidget } from '../Widgets/MetaWidget';
import { OverviewWidget } from '../Widgets/OverviewWidget';
import { PromoCardWidget } from '../Widgets/PromoCardWidget';
import {
  PurchasePanelWidget,
  KlarnaCard,
  VirginLovesCard,
  type Props as PurchasePanelProps,
} from '../Widgets/PurchasePanelWidget';
import { FeaturedReviewsWidget, CustomerReviewsWidget } from '../Widgets/ReviewsWidget';
import { ReviewsOverlay } from '../Reviews/ReviewsOverlay';
import { useAddToBasket } from '../AddToBasket';

import { useHasQueryParam } from '@virginexperiencedays/hooks';
import { useDyCustomEventListener } from '@virginexperiencedays/components-v2/src/pages/product/DyPromoBanners';
import { isEnableServer } from '@virginexperiencedays/feature-flags/src/useFlags';

import {
  additionalDetailsExtractor,
  categoriesExtractor,
  overviewExtractor,
  type AdditionalDetails,
  collectionChoicesExtractor,
} from './extractors';

import Stripe from '../Stripe';

import type { PageUIProps } from '../Page';
import type { Location } from '@virginexperiencedays/products';
import type { Product } from '@virginexperiencedays/products';
import type { AddToBasketWidgetProps } from '../Widgets/AddToBasketWidget';
import type { Props as PRWProps } from '../Widgets/ProductRecommendationWidget';

import { CollectionProductChoicesWidget } from '../Widgets/CollectionProductChoicesWidget';

const ProductRecommendationWidget = dynamic<PRWProps>(
  () =>
    import('../Widgets/ProductRecommendationWidget').then(
      (module) => module.ProductRecommendationWidget
    ),
  { ssr: false }
);

const DyPromoCodeSignUpBanner = dynamic<HTMLAttributes<HTMLElement>>(
  () =>
    import('@virginexperiencedays/components-v2/src/pages/product/DyPromoBanners').then(
      (module) => module.PromoCodeSignUpBanner
    ),
  { ssr: false }
);

const DySimplePromoBanner = dynamic<HTMLAttributes<HTMLElement>>(
  () =>
    import('@virginexperiencedays/components-v2/src/pages/product/DyPromoBanners').then(
      (module) => module.SimplePromoBanner
    ),
  { ssr: false }
);

const isNewBundlesMapEnabled = isEnableServer('FF_new_bundles_map');

export function PageUI({
  product,
  productPromo,
  isActive,
  flags,
  reviews,
  handleQuickBasket,
  isCollectionProduct,
  slugNameMap,
  trackingData,
}: Readonly<PageUIProps>) {
  const router = useRouter();
  const isSP0 = router.query.sp === '0';

  /**
   * Ensure all hooks are implemented following the rules of hooks and named descriptively.
   */
  // e.g. const sortedReviews = useSortReviews(reviewsList);
  const { ref: locationsMapRef, scrollToRef: scrollTolocationMap } = useScrollToRef();
  const { width: windowWidth } = useWindowWidth();
  const displayGatedPromo = useDyCustomEventListener('dy:promo-code-mount');
  const displaySimplePromo = useDyCustomEventListener('dy:promo-simple-mount');

  const [reviewsOpen, setReviewsOpen] = useState(false);

  const {
    handleAddToBasket,
    outOfStock,
    error: basketError,
  } = useAddToBasket({
    product,
    flags,
    quickBasket: true,
    handleQuickBasket,
  });

  const showMobileWidgets = windowWidth < screens.lg;

  /**
   * Implement the props extractor functions, found in `./extractors`, here.
   */
  // e.g. const exampleProps = examplePropsExtractor(product);
  const { title, locations, sku } = product;

  const overview = overviewExtractor(product);
  const additionalDetails = additionalDetailsExtractor(product);
  const categories = categoriesExtractor(product, { slugNameMap });

  const { collectionChoices = [], choicesCount = 0 } = collectionChoicesExtractor(product);

  const isVirginLovesProduct = product?.facets?.features?.includes('virgin-loves');
  const { currentPrice, roundedPercentOff } = getProductCardPrices({
    displayPrice: product.price.displayPrice,
    rrp: product.price.rrp,
    percentOff: product.price.percentOff,
  });

  const hidePurchasePanel = isSP0 || !currentPrice;

  const onStripeLoad = ({ paymentMethod }) => {
    trackPdpPayment({
      ...trackingData,
      module: PdpModules.PurchasePanel,
      paymentMethod,
      action: 'load',
    });
  };
  const onStripeClick = ({ paymentMethod }) => {
    trackPdpPayment({
      ...trackingData,
      module: PdpModules.PurchasePanel,
      paymentMethod,
      action: 'click',
    });
  };
  const onStripeSuccess = ({ paymentMethod }) => {
    trackPdpPayment({
      ...trackingData,
      module: PdpModules.PurchasePanel,
      paymentMethod,
      action: 'success',
    });
  };
  const onStripeError = ({ paymentMethod }) => {
    trackPdpPayment({
      ...trackingData,
      module: PdpModules.PurchasePanel,
      paymentMethod,
      action: 'error',
    });
  };

  const addToBasketProps: Omit<AddToBasketWidgetProps, 'onClick'> = {
    error: basketError,
    isActive,
    outOfStock,
  };

  const purchasePanelProps: PurchasePanelProps = {
    product,
    hidePurchasePanel,
    addToBasket: {
      ...addToBasketProps,
      onClick: () => {
        // track click
        trackPdp({
          ...trackingData,
          interaction: PdpInteraction.ClickAddToBasket,
          module: PdpModules.PurchasePanel,
          moduleTitle: null,
        });

        // open quick basket
        return handleAddToBasket?.();
      },
    },
    onStripeLoad,
    onStripeClick,
    onStripeSuccess,
    onStripeError,
  };

  return (
    <Container data-testid={DataTestId.PageLayout} className="flex flex-col gap-6">
      {/* top content */}
      <div data-testid={DataTestId.TopContent} className="relative grid grid-cols-1 gap-6">
        <section className="lg:col-start-1">
          <MediaGridWidget
            product={product}
            hideAddToBasket={hidePurchasePanel}
            addToBasket={{
              ...addToBasketProps,
              onClick: async () => {
                // track click
                trackPdp({
                  ...trackingData,
                  interaction: PdpInteraction.ClickAddToBasket,
                  module: PdpModules.MediaGrid,
                  moduleTitle: null,
                });

                // open quick basket
                return handleAddToBasket?.();
              },
            }}
            onClickImage={({ currentIndex, totalSlides }) => {
              trackPdpMediaGrid({
                ...trackingData,
                currentIndex,
                total: totalSlides,
                action: 'image',
                gesture: 'click',
              });
            }}
            onClickNav={({ currentIndex, totalSlides, dir }) => {
              trackPdpMediaGrid({
                ...trackingData,
                currentIndex,
                total: totalSlides,
                action: dir,
                gesture: 'click',
              });
            }}
            onSwipeNav={({ currentIndex, totalSlides, dir }) => {
              trackPdpMediaGrid({
                ...trackingData,
                currentIndex,
                total: totalSlides,
                action: dir,
                gesture: 'swipe',
              });
            }}
          />
        </section>

        <section className="-mt-2 flex flex-col gap-0 lg:col-start-1 lg:row-start-1 lg:-mb-2 lg:mt-0 lg:gap-4">
          <div className="flex flex-col gap-2">
            {/* Title */}
            <DisplayHeading
              data-testid={DataTestId.Title}
              className={cn(
                title.length < 95 ? 'text-xl' : 'text-lg',
                'font-semibold leading-tight lg:text-[28px] lg:font-bold'
              )}
            >
              {title}
            </DisplayHeading>

            {/* Meta Summary */}
            <MetaWidget
              count={reviews?.count}
              averageRating={reviews?.averageRating}
              averageRatingDisplay={reviews?.averageRatingDisplay}
              data-testid={DataTestId.ReviewsSummary}
              onReviewCountClick={() => {
                trackPdp({
                  ...trackingData,
                  interaction: PdpInteraction.ClickAllReviews,
                  module: PdpModules.ReviewSummary,
                  moduleTitle: null,
                });
                setReviewsOpen(true);
              }}
            />
          </div>

          {showMobileWidgets && !isSP0 && (
            <Stripe
              productId={product?.id}
              productSlug={product?.slug}
              deliveryOptions={product?.deliveryOptions}
              displayPrice={product?.price?.displayPrice}
              className="mt-4 empty:hidden"
              onLoad={onStripeLoad}
              onClick={onStripeClick}
              onSuccess={onStripeSuccess}
              onError={onStripeError}
            />
          )}

          {isVirginLovesProduct && <VirginLovesCard className="mt-4 flex lg:hidden" />}
        </section>

        {/* desktop purchase panel */}
        <PurchasePanelWidget {...purchasePanelProps} className="hidden lg:flex" />

        <section className="flex flex-col gap-6 lg:col-start-1">
          {!!productPromo?.key && (
            <PromoCardWidget promo={productPromo.key} percentOff={roundedPercentOff} />
          )}

          {displayGatedPromo && <DyPromoCodeSignUpBanner className="hidden" />}
          {displaySimplePromo && <DySimplePromoBanner className="hidden" />}

          <OverviewWidget
            {...overview}
            onLocationClick={() => {
              trackPdp({
                ...trackingData,
                interaction: PdpInteraction.ClickScrollToLocations,
                module: PdpModules.UspGrid,
                moduleTitle: null,
              });
              scrollTolocationMap();
            }}
            onAboutExpandCollapse={({ isExpanded, moduleTitle }) => {
              const interaction = isExpanded
                ? PdpInteraction.ClickReadLess
                : PdpInteraction.ClickReadMore;

              trackPdp({
                ...trackingData,
                interaction,
                module: PdpModules.About,
                moduleTitle,
              });
            }}
          />

          {showMobileWidgets && !isSP0 && (
            <KlarnaCard
              displayPrice={product?.price?.displayPrice}
              className="mx-auto max-w-80 lg:hidden"
              bordered
            />
          )}

          {isCollectionProduct && (
            <CollectionProductChoicesWidget
              productTitle={title}
              collections={collectionChoices}
              choicesCount={choicesCount}
              className="pt-4"
              trackingData={trackingData}
            />
          )}
        </section>

        {!isCollectionProduct && (
          <>
            <PdpTopContent
              additionalDetails={additionalDetails}
              isBundle={isNewBundlesMapEnabled && product?.isBundle}
              locationsMapRef={locationsMapRef}
              locations={locations}
              voucherPartTitles={isNewBundlesMapEnabled ? product?.voucherPartTitles : undefined}
              reviews={reviews}
              sku={sku}
              trackingData={trackingData}
            />

            {showMobileWidgets && (
              <PdpBottomContent
                reviews={reviews}
                setReviewsOpen={setReviewsOpen}
                sku={sku}
                trackingData={trackingData}
              />
            )}
          </>
        )}

        {isCollectionProduct && showMobileWidgets && <CollectionProductContent sku={sku} />}
      </div>

      {isCollectionProduct && !showMobileWidgets && <CollectionProductContent sku={sku} />}

      {!isCollectionProduct && !showMobileWidgets && (
        <PdpBottomContent
          reviews={reviews}
          setReviewsOpen={setReviewsOpen}
          sku={sku}
          trackingData={trackingData}
        />
      )}

      {/* mobile purchase panel */}
      <PurchasePanelWidget {...purchasePanelProps} className="lg:hidden" />

      {/* more from category */}
      <CategoryPillsWidget
        items={categories}
        onClick={({ text }) => {
          trackPdp({
            ...trackingData,
            interaction: PdpInteraction.ClickCategory,
            module: PdpModules.CategoryPills,
            moduleTitle: text,
          });
        }}
        data-testid={DataTestId.CategoryPills}
      />

      {/* modal/drawers */}
      <ReviewsOverlay
        averageRating={reviews?.averageRatingDisplay}
        count={reviews?.count}
        reviews={reviews?.reviews}
        open={reviewsOpen}
        setOpen={setReviewsOpen}
        onExpandCollapse={({
          currentIndex,
          total,
          isExpanded,
          rating,
          dateReviewed,
          moduleTitle,
          source,
        }) => {
          trackPdpReview({
            ...trackingData,
            currentIndex,
            total,
            action: isExpanded ? 'collapse' : 'expand',
            type: 'all',
            rating,
            dateReviewed,
            moduleTitle,
            source,
          });
        }}
      />
    </Container>
  );
}

/**
 * The PDP top content component is made up of a number of sections currently specific to
 * the `/product/*` pages. It is wrapped in a fragment to allow its layout to be controlled
 * by the parent component.
 */
type PdpTopContentProps = {
  additionalDetails: AdditionalDetails;
  isBundle?: boolean;
  locationsMapRef: RefObject<HTMLDivElement>;
  locations: Location[];
  reviews: PageUIProps['reviews'];
  sku: Product['sku'];
  trackingData: PageUIProps['trackingData'];
  voucherPartTitles?: Product['voucherPartTitles'];
};

function PdpTopContent({
  additionalDetails,
  isBundle,
  locationsMapRef,
  locations,
  reviews,
  sku,
  trackingData,
  voucherPartTitles,
}: PdpTopContentProps) {
  return (
    <>
      <FeaturedReviewsWidget
        featured={reviews?.featured}
        className="flex flex-col gap-4 lg:col-start-1 lg:mb-2"
        data-testid={DataTestId.FeaturedReviews}
        onExpandCollapse={({
          currentIndex,
          total,
          isExpanded,
          rating,
          dateReviewed,
          moduleTitle,
        }) => {
          trackPdpReview({
            ...trackingData,
            currentIndex,
            total,
            action: isExpanded ? 'collapse' : 'expand',
            type: 'featured',
            rating,
            dateReviewed,
            moduleTitle,
          });
        }}
      />

      <section className="lg:col-start-1">
        <AdditionalDetailsWidget
          additionalDetails={additionalDetails}
          hasLocationsItinerary={isBundle}
          locationsMapRef={locationsMapRef}
          locations={locations}
          locationsTitles={voucherPartTitles}
          sku={sku}
          onAccordionToggle={({ isExpanded, text }) => {
            const interaction = isExpanded
              ? PdpInteraction.ClickAccordionCollapse
              : PdpInteraction.ClickAccordionExpand;

            trackPdp({
              ...trackingData,
              interaction,
              module: PdpModules.AdditionalDetails,
              moduleTitle: text,
            });
          }}
          onExpandCollapse={({ isExpanded, moduleTitle }) => {
            const interaction = isExpanded
              ? PdpInteraction.ClickReadLess
              : PdpInteraction.ClickReadMore;

            trackPdp({
              ...trackingData,
              interaction,
              module: PdpModules.AdditionalDetails,
              moduleTitle,
            });
          }}
          onClickMap={({ text }) => {
            trackPdp({
              ...trackingData,
              interaction: PdpInteraction.ClickMap,
              module: PdpModules.Locations,
              moduleTitle: text,
            });
          }}
          onClickLocationSelect={({ text }) => {
            trackPdp({
              ...trackingData,
              interaction: PdpInteraction.ClickLocationSelect,
              module: PdpModules.Locations,
              moduleTitle: text,
            });
          }}
          onSelectLocation={({ text }) => {
            trackPdp({
              ...trackingData,
              interaction: PdpInteraction.SelectLocation,
              module: PdpModules.Locations,
              moduleTitle: text,
            });
          }}
        />
      </section>
    </>
  );
}

/**
 * The Collection Product content component is made up of a number of sections currently
 * specific to the `/collection/*` pages. It is wrapped in a `div` as a fully self-contained component.
 * It should only be rendered when the `isCollectionProduct` prop is `true`.
 */
type CollectionProductContentProps = {
  sku: Product['sku'];
} & HTMLAttributes<HTMLDivElement>;
function CollectionProductContent({ className, sku }: CollectionProductContentProps) {
  return (
    <div
      data-testid={DataTestId.BottomContent}
      className={cn(
        'flex flex-col gap-6 border-b border-solid border-border-neutral pb-6 dark:border-persistent-text-light',
        className
      )}
    >
      <Section>
        <SectionHeading>You May Also Like</SectionHeading>
        <ProductRecommendationWidget sku={sku} />
      </Section>
    </div>
  );
}

/**
 * The PDP bottom content component is made up of a number of sections currently specific to
 * the `/product/*` pages. It is wrapped in a `div` as a fully self-contained component.
 * It should only be rendered when the `isCollectionProduct` prop is `false`.
 */
type PdpBottomContentProps = {
  className?: string;
  reviews: PageUIProps['reviews'];
  sku: Product['sku'];
  setReviewsOpen: (value: boolean) => void;
  trackingData: PageUIProps['trackingData'];
};

function PdpBottomContent({
  className,
  reviews,
  setReviewsOpen,
  sku,
  trackingData,
}: PdpBottomContentProps) {
  const { status: gsStatus, hasParam: isGoogleShopping } = useHasQueryParam('opti_key', 'pla');

  return (
    <div data-testid={DataTestId.BottomContent} className={cn('flex flex-col gap-6', className)}>
      {gsStatus === 'ready' && !isGoogleShopping && (
        <Section>
          <SectionHeading>You May Also Like</SectionHeading>
          <ProductRecommendationWidget sku={sku} />
        </Section>
      )}

      {reviews?.count && (
        <CustomerReviewsWidget
          className="lg:last:border-b lg:last:pb-6"
          data-testid={DataTestId.CustomerReviews}
          averageRating={reviews?.averageRating}
          averageRatingDisplay={reviews?.averageRatingDisplay}
          count={reviews?.count}
          reviews={reviews?.reviews}
          onClickAllReviews={() => {
            trackPdp({
              ...trackingData,
              interaction: PdpInteraction.ClickAllReviews,
              module: PdpModules.CustomerReviews,
              moduleTitle: null,
            });
            setReviewsOpen(true);
          }}
          onExpandCollapse={({
            currentIndex,
            total,
            isExpanded,
            rating,
            dateReviewed,
            moduleTitle,
          }) => {
            trackPdpReview({
              ...trackingData,
              currentIndex,
              total,
              action: isExpanded ? 'collapse' : 'expand',
              type: 'all',
              rating,
              dateReviewed,
              moduleTitle,
            });
          }}
        />
      )}
    </div>
  );
}

/**
 * The data-testid values for the PDP page layout and its components. These should be passed
 * to the component as a prop and not hardcoded into the component itself. Achieve this by
 * utilising the `HTMLAttributes<...>` interface from `react` and the `data-testid` attribute.
 *
 * The enum is only exported for the purpose of testing.
 */
export enum DataTestId {
  // sections and layout
  PageLayout = 'pdp-page-layout',
  TopContent = 'pdp-top-content',
  BottomContent = 'pdp-bottom-content',

  // components
  Accordion = 'pdp-accordion',
  AccordionItem = 'pdp-accordion-item',
  AboutExperience = 'pdp-about-experience',
  Availability = 'pdp-availability',
  CategoryPills = 'pdp-category-pills',
  CustomerReviews = 'pdp-customer-reviews',
  FeaturedReviews = 'pdp-featured-reviews',
  Guidelines = 'pdp-guidelines',
  HowItWorks = 'pdp-how-it-works',
  Insurance = 'pdp-insurance',
  Location = 'pdp-location',
  MediaGrid = 'pdp-media-grid',
  OnTheDay = 'pdp-on-the-day',
  PaymentProviders = 'pdp-payment-providers',
  PurchasePanel = 'pdp-purchase-panel',
  PurchasePanelPromo = 'pdp-purchase-panel-promo',
  PurchasePanelWidgetContainer = 'pdp-purchase-panel-widget-container',
  ReviewsSummary = 'pdp-reviews-summary',
  Title = 'pdp-title',
  UspGrid = 'pdp-usp-grid',
  VirginLoves = 'pdp-virgin-loves',
  WhatsIncluded = 'pdp-whats-included',
  YouMayAlsoLike = 'pdp-you-may-also-like',

  // modals and drawers
  MediaLightBox = 'pdp-media-light-box',

  // collection product components
  CollectionWrapper = 'pdp-collection-wrapper',
  CollectionChoices = 'pdp-collection-choices',
  CollectionTitleSummary = 'pdp-collection-title-summary',
  CollectionChoicesCategory = 'pdp-collection-choices-category',
  CollectionImage = 'pdp-collection-image', // this may change once we have finalised designs
}
